import React, { useState, useMemo } from 'react';
import { useQuery } from 'react-query';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  FiDownload, FiExternalLink, FiCopy, FiPrinter,
  FiGrid, FiList, FiSearch, FiChevronDown, FiChevronUp
} from 'react-icons/fi';
import { HiQrcode } from 'react-icons/hi';
import { api } from '../../api';
import toast from 'react-hot-toast';

const QRCodeCard = ({ item, baseUrl }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('URL copied to clipboard');
  };
  
  const downloadQR = (format = 'png') => {
    const link = document.createElement('a');
    link.download = `qr-${item.slug}.${format}`;
    
    if (format === 'png') {
      link.href = item.qr;
    } else {
      // Convert data URL to SVG for SVG download
      const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="200" height="200">
          <image href="${item.qr}" width="200" height="200"/>
        </svg>
      `;
      const blob = new Blob([svg], { type: 'image/svg+xml' });
      link.href = URL.createObjectURL(blob);
    }
    
    link.click();
  };
  
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden"
    >
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <h3 className="font-medium text-mountain-gray-900">{item.name}</h3>
            <p className="text-sm text-gray-500 mt-1">{item.type === 'category' ? 'Category' : 'Section'}</p>
          </div>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            {isExpanded ? <FiChevronUp /> : <FiChevronDown />}
          </button>
        </div>
        
        <div className="flex items-center space-x-2 text-xs">
          <span className="text-gray-500">URL:</span>
          <code className="flex-1 bg-gray-100 px-2 py-1 rounded font-mono truncate">
            {item.url}
          </code>
          <button
            onClick={() => copyToClipboard(item.url)}
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Copy URL"
          >
            <FiCopy className="w-4 h-4" />
          </button>
          <a
            href={item.url}
            target="_blank"
            rel="noopener noreferrer"
            className="p-1 hover:bg-gray-100 rounded transition-colors"
            title="Preview"
          >
            <FiExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
      
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0 }}
            animate={{ height: 'auto' }}
            exit={{ height: 0 }}
            className="border-t border-gray-200 overflow-hidden"
          >
            <div className="p-4 bg-gray-50">
              <div className="flex flex-col items-center">
                <img 
                  src={item.qr} 
                  alt={`QR Code for ${item.name}`}
                  className="w-48 h-48 mb-4"
                />
                <div className="flex space-x-2">
                  <button
                    onClick={() => downloadQR('png')}
                    className="flex items-center px-3 py-2 text-sm bg-lodge-brown-600 text-white rounded-lg hover:bg-lodge-brown-700"
                  >
                    <FiDownload className="w-4 h-4 mr-1" />
                    Download PNG
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

const PrintableQRSheet = ({ items }) => {
  return (
    <div className="hidden print:block">
      <style>
        {`
          @media print {
            @page { margin: 0.5in; }
            body { margin: 0; }
            .page-break { page-break-after: always; }
            .qr-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
            .qr-item { text-align: center; padding: 20px; border: 1px solid #ddd; }
            .qr-item img { width: 150px; height: 150px; margin: 0 auto 10px; }
            .qr-item h3 { font-size: 14px; margin: 5px 0; }
            .qr-item p { font-size: 10px; color: #666; }
          }
        `}
      </style>
      <div className="qr-grid">
        {items.map((item, index) => (
          <div key={item.slug} className={`qr-item ${(index + 1) % 9 === 0 ? 'page-break' : ''}`}>
            <img src={item.qr} alt={`QR Code for ${item.name}`} />
            <h3>{item.name}</h3>
            <p>{item.type === 'category' ? 'Category' : 'Section'}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const AdminQRCodes = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [baseUrl, setBaseUrl] = useState(window.location.origin);
  
  const { data: qrCodesData, isLoading, refetch } = useQuery(
    ['qrCodes', baseUrl],
    () => api.getAllQRCodes(baseUrl),
    {
      onError: () => toast.error('Failed to load QR codes')
    }
  );
  
  const qrCodes = qrCodesData?.data || [];
  
  // Filter and search QR codes
  const filteredQRCodes = useMemo(() => {
    let filtered = qrCodes;
    
    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(item => item.type === filterType);
    }
    
    // Search
    if (searchQuery) {
      filtered = filtered.filter(item =>
        item.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        item.slug.toLowerCase().includes(searchQuery.toLowerCase())
      );
    }
    
    return filtered;
  }, [qrCodes, filterType, searchQuery]);
  
  // Group by category for better organization
  const groupedQRCodes = useMemo(() => {
    const groups = {
      categories: filteredQRCodes.filter(item => item.type === 'category'),
      sections: filteredQRCodes.filter(item => item.type === 'section')
    };
    return groups;
  }, [filteredQRCodes]);
  
  const handlePrint = () => {
    window.print();
  };
  
  const handleDownloadAll = async () => {
    toast.loading('Preparing download...');
    
    // Create a zip file would be ideal here, but for now we'll download them sequentially
    for (const item of filteredQRCodes) {
      const link = document.createElement('a');
      link.download = `qr-${item.slug}.png`;
      link.href = item.qr;
      link.click();
      await new Promise(resolve => setTimeout(resolve, 500)); // Small delay between downloads
    }
    
    toast.dismiss();
    toast.success('Downloads started');
  };
  
  const handleBaseUrlChange = (e) => {
    setBaseUrl(e.target.value);
  };
  
  const handleBaseUrlSubmit = (e) => {
    e.preventDefault();
    refetch();
    toast.success('QR codes regenerated with new base URL');
  };
  
  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-display text-lodge-brown-800">QR Code Management</h1>
        <p className="text-mountain-gray-600 mt-2">
          Generate and manage QR codes for easy access to guidebook content
        </p>
      </div>
      
      {/* Base URL Configuration */}
      <div className="bg-sky-blue-50 rounded-lg p-4 mb-6">
        <form onSubmit={handleBaseUrlSubmit} className="flex items-end space-x-4">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Base URL for QR Codes
            </label>
            <input
              type="url"
              value={baseUrl}
              onChange={handleBaseUrlChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
              placeholder="https://example.com"
            />
            <p className="text-xs text-gray-500 mt-1">
              This URL will be used as the base for all QR code links
            </p>
          </div>
          <button
            type="submit"
            className="btn-secondary"
          >
            Update QR Codes
          </button>
        </form>
      </div>
      
      {/* Controls Bar */}
      <div className="flex flex-wrap justify-between items-center gap-4 mb-6">
        <div className="flex items-center space-x-4">
          {/* Search */}
          <div className="relative">
            <FiSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
            />
          </div>
          
          {/* Filter */}
          <select
            value={filterType}
            onChange={(e) => setFilterType(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-lodge-brown-500 focus:border-lodge-brown-500"
          >
            <option value="all">All Types</option>
            <option value="category">Categories Only</option>
            <option value="section">Sections Only</option>
          </select>
          
          {/* View Mode */}
          <div className="flex border border-gray-300 rounded-lg overflow-hidden">
            <button
              onClick={() => setViewMode('grid')}
              className={`px-3 py-2 ${viewMode === 'grid' ? 'bg-lodge-brown-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              <FiGrid className="w-4 h-4" />
            </button>
            <button
              onClick={() => setViewMode('list')}
              className={`px-3 py-2 ${viewMode === 'list' ? 'bg-lodge-brown-600 text-white' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
            >
              <FiList className="w-4 h-4" />
            </button>
          </div>
        </div>
        
        {/* Actions */}
        <div className="flex items-center space-x-2">
          <button
            onClick={handlePrint}
            className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <FiPrinter className="w-4 h-4 mr-2" />
            Print Sheet
          </button>
          <button
            onClick={handleDownloadAll}
            className="flex items-center px-4 py-2 bg-lodge-brown-600 text-white rounded-lg hover:bg-lodge-brown-700"
          >
            <FiDownload className="w-4 h-4 mr-2" />
            Download All
          </button>
        </div>
      </div>
      
      {/* Content */}
      {isLoading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-lodge-brown-600"></div>
        </div>
      ) : filteredQRCodes.length === 0 ? (
        <div className="text-center py-12 bg-gray-50 rounded-xl">
          <HiQrcode className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">No QR codes found</p>
        </div>
      ) : (
        <div className="space-y-8">
          {/* Categories Section */}
          {groupedQRCodes.categories.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-mountain-gray-900 mb-4">
                Category QR Codes ({groupedQRCodes.categories.length})
              </h2>
              <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}>
                {groupedQRCodes.categories.map(item => (
                  <QRCodeCard key={item.slug} item={item} baseUrl={baseUrl} />
                ))}
              </div>
            </div>
          )}
          
          {/* Sections */}
          {groupedQRCodes.sections.length > 0 && (
            <div>
              <h2 className="text-xl font-semibold text-mountain-gray-900 mb-4">
                Section QR Codes ({groupedQRCodes.sections.length})
              </h2>
              <div className={viewMode === 'grid' ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4' : 'space-y-4'}>
                {groupedQRCodes.sections.map(item => (
                  <QRCodeCard key={item.slug} item={item} baseUrl={baseUrl} />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
      
      {/* Printable Sheet (hidden, only shows when printing) */}
      <PrintableQRSheet items={filteredQRCodes} />
    </div>
  );
};

export default AdminQRCodes;